/**
 * Phase 45 Plan 45-03 (D-02) — assignee picker for TaskDetailSlideOver.
 *
 * Petra UAT 2026-05-19 — admin / PM can pick from project members to
 * assign / reassign / unassign. Dropdown lists every user with a non-
 * deleted ProjectAssignment on this project; "Unassign" sets assigneeId
 * to null (drops to the free-market backlog). Backend enforces the full
 * D-02 / D-03 reassign rules — any 4xx surfaces as a toast.
 *
 * Phase 66 EAP-06 — tabbed picker when isExternalTask.
 * When isExternalTask=true, a tab bar (Internal / External) appears at
 * the top of the dropdown. The External tab lists project external users
 * via the existing useProjectExternalMembers hook; picking one fires the
 * reassign mutation with assigneeKind='external_user'. Internal tasks
 * (isExternalTask=false or undefined) use the original single-list
 * behaviour — no tab bar, no external hook mount.
 *
 * The picker uses a native <details> element for the dropdown so a11y +
 * keyboard + outside-click-to-close work without a custom popover lib.
 * It blends with the slide-over header's pill aesthetic via the same
 * `bg-ms-canvas border rounded-full` chip wrapper.
 *
 * RBAC: since MKS-132 this component renders for EVERY internal viewer
 * (`!isExternalAudience` in TaskDetailSlideOver), not only admin / PM —
 * the backend enforces the reassign rules and a refusal surfaces as a
 * toast. The header comment used to claim the older admin/PM-only gate.
 *
 * MKS-513 — that widening is why the External tab was empty for everyone
 * but an admin or the PM: `useProjectExternalMembers` hits the admin/PM-
 * gated `GET /projects/:id/external-members`, and its 403 rendered as "No
 * external members to assign.". Behind `mks_513_external_assignee_picker`
 * the tab reads the member-readable
 * `GET /projects/:id/assignable-external-members` instead (names only — no
 * e-mail), tells loading / failed / genuinely empty apart, and marks the
 * current external assignee. Flag off keeps all of that exactly as it was.
 */
import { useState, useCallback, useRef, type ComponentRef } from 'react';
import { Avatar } from '../ui/Avatar';
import { useProjectAssignments } from '../../hooks/useProjects';
import {
  useAssignableExternalMembers,
  useProjectExternalMembers,
} from '../../hooks/useProjectExternalMembers';
import { useReassignTask } from '../../hooks/useReassignTask';
import { useFlag } from '../../services/flagsContext';
import { useToast } from '../ui/Toast';

export interface AssigneePickerProps {
  taskId: number;
  projectId: number;
  currentAssigneeId: number | null;
  currentAssigneeName: string | null;
  /** Phase 66 EAP-06 — shows Internal/External tab bar when true. */
  isExternalTask?: boolean;
  /** Phase 66 EAP-06 — which kind is currently assigned; drives initial tab. */
  currentAssigneeKind?: 'user' | 'external_user' | null;
  /** 260915-i0x — avatar slot of the current assignee; null for external. */
  currentAssigneeColorSeq?: number | null;
  /** MKS-203 — effective avatar photo of the current assignee; null for external. */
  currentAssigneeAvatarUrl?: string | null;
  /**
   * MKS-513 — `external_users.id` of the current assignee, null when the task
   * is unassigned or assigned to an internal user. Marks the matching row on
   * the External tab as current, mirroring the internal branch.
   */
  currentExternalAssigneeId?: number | null;
}

export function AssigneePicker({
  taskId,
  projectId,
  currentAssigneeId,
  currentAssigneeName,
  isExternalTask,
  currentAssigneeKind,
  currentAssigneeColorSeq,
  currentAssigneeAvatarUrl,
  currentExternalAssigneeId,
}: AssigneePickerProps) {
  const { data: assignments = [], isLoading } = useProjectAssignments(projectId);
  // MKS-513 — exactly one of the two external queries is ever enabled, so
  // the flag-off branch keeps making precisely the request it makes today.
  const externalPicker = useFlag('mks_513_external_assignee_picker');
  // Phase 66: only fetch external members for external tasks; avoids a
  // spurious 403 from requireAdminOrProjectPM for non-external tasks.
  const legacyExternal = useProjectExternalMembers(
    projectId,
    !!isExternalTask && !externalPicker,
  );
  const assignableExternal = useAssignableExternalMembers(
    projectId,
    !!isExternalTask && externalPicker,
  );
  // Flag off: no loading or error state — a failed fetch falls through to the
  // empty list, which is the pre-MKS-513 behaviour this branch preserves.
  const external = externalPicker
    ? {
        rows: assignableExternal.data ?? [],
        isLoading: assignableExternal.isLoading,
        isError: assignableExternal.isError,
      }
    : { rows: legacyExternal.data ?? [], isLoading: false, isError: false };
  const reassign = useReassignTask();
  const showToast = useToast();
  const detailsRef = useRef<ComponentRef<'details'> | null>(null);

  // Active tab defaults to 'external' when the task is already assigned to
  // an external user so the current assignee is visible on first open.
  const [activeTab, setActiveTab] = useState<'internal' | 'external'>(
    currentAssigneeKind === 'external_user' ? 'external' : 'internal',
  );

  // Dedupe by user.id — a user may have multiple ProjectAssignment rows
  // (different role, date range). Surface each unique user once with their
  // display name from the assignment.user join.
  const internalMembers = (() => {
    const seen = new Map<
      number,
      { id: number; name: string; colorSeq: number | null; avatarUrl: string | null }
    >();
    for (const a of assignments) {
      if (!a.user) continue;
      if (seen.has(a.user.id)) continue;
      // 260915-i0x — carry the avatar slot so picker rows match the board.
      seen.set(a.user.id, {
        id: a.user.id,
        name: a.user.name,
        colorSeq: a.user.avatarColorSeq ?? null,
        // MKS-203 — carry the effective avatar photo likewise.
        avatarUrl: a.user.avatarUrl ?? null,
      });
    }
    return Array.from(seen.values()).sort((x, y) => x.name.localeCompare(y.name));
  })();

  const externalMemberList = external.rows
    .map((m) => ({
      id: m.id,
      // Project convention: "Lastname Firstname" in pickers (matches the
      // internal tab, whose names come pre-formatted from the assignments
      // endpoint lastname-first).
      name: `${m.lastName} ${m.firstName}`.trim(),
    }))
    .sort((x, y) => x.name.localeCompare(y.name));

  const close = useCallback(() => {
    if (detailsRef.current) detailsRef.current.open = false;
  }, []);

  const handlePick = useCallback(
    (nextId: number | null, kind: 'user' | 'external_user') => {
      reassign.mutate(
        {
          taskId,
          assigneeId: kind === 'user' ? nextId : null,
          externalAssigneeId: kind === 'external_user' ? nextId : null,
          assigneeKind: kind,
          projectId,
        },
        {
          onError: (err) => {
            const msg = err instanceof Error ? err.message : 'Reassignment failed';
            showToast(msg, 'error');
          },
          onSuccess: () => close(),
        },
      );
    },
    [reassign, taskId, projectId, showToast, close],
  );

  const chipLabel = currentAssigneeName ?? 'Unassigned';
  const chipColorClass = currentAssigneeName
    ? 'text-ms-text-secondary'
    : 'text-ms-text-muted italic';

  // Unassign is enabled when something is currently assigned (either kind).
  const isAssigned =
    currentAssigneeId !== null ||
    (isExternalTask && currentAssigneeKind === 'external_user');

  return (
    <details
      ref={detailsRef}
      className="relative"
      data-testid="task-detail-assignee-picker"
    >
      <summary
        className={`list-none cursor-pointer flex items-center gap-1.5 px-2 py-1 bg-ms-canvas border border-ms-border-subtle rounded-full hover:bg-ms-surface-2 ${
          reassign.isPending ? 'opacity-60 pointer-events-none' : ''
        }`}
        aria-label={
          currentAssigneeName
            ? `Change assignee from ${currentAssigneeName}`
            : 'Assign someone'
        }
      >
        {currentAssigneeName ? (
          <Avatar
            name={currentAssigneeName}
            colorSeq={currentAssigneeColorSeq}
            avatarUrl={currentAssigneeAvatarUrl}
            size="xs"
          />
        ) : (
          <span
            aria-hidden="true"
            className="w-5 h-5 rounded-full border border-dashed border-ms-border flex items-center justify-center text-ms-text-muted text-[10px]"
          >
            ?
          </span>
        )}
        <span className={`text-[11px] font-medium ${chipColorClass}`}>{chipLabel}</span>
        {isExternalTask && currentAssigneeKind === 'external_user' && (
          <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700">
            External
          </span>
        )}
        <svg
          aria-hidden="true"
          className="h-3 w-3 opacity-70"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </summary>

      <div
        className="absolute right-0 top-full mt-1 z-20 min-w-[240px] max-h-72 overflow-y-auto bg-ms-surface border border-ms-border rounded-lg shadow-lg py-1"
        role="menu"
      >
        {/* Phase 66 EAP-06 — tab bar for external tasks */}
        {isExternalTask && (
          <div
            className="flex border-b border-ms-border-subtle"
            role="tablist"
            aria-label="Assignee type"
          >
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'internal'}
              onClick={() => setActiveTab('internal')}
              className={`flex-1 px-3 py-1.5 text-[11px] font-medium ${
                activeTab === 'internal'
                  ? 'text-primary-600 border-b-2 border-primary-500'
                  : 'text-ms-text-muted hover:text-ms-text-secondary'
              }`}
            >
              Internal
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'external'}
              onClick={() => setActiveTab('external')}
              className={`flex-1 px-3 py-1.5 text-[11px] font-medium ${
                activeTab === 'external'
                  ? 'text-primary-600 border-b-2 border-primary-500'
                  : 'text-ms-text-muted hover:text-ms-text-secondary'
              }`}
            >
              External
            </button>
          </div>
        )}

        {/* Unassign */}
        <button
          type="button"
          role="menuitem"
          onClick={() => handlePick(null, currentAssigneeKind === 'external_user' ? 'external_user' : 'user')}
          disabled={!isAssigned || reassign.isPending}
          className="w-full flex items-center gap-2 px-3 py-1.5 text-[12px] text-ms-text-secondary hover:bg-ms-canvas disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span
            aria-hidden="true"
            className="w-5 h-5 rounded-full border border-dashed border-ms-border flex items-center justify-center text-ms-text-muted text-[10px]"
          >
            ✕
          </span>
          <span className="italic">Unassign</span>
        </button>

        <div className="border-t border-ms-border-subtle my-1" />

        {/* Internal members list — shown when not external task OR on internal tab */}
        {(!isExternalTask || activeTab === 'internal') && (
          isLoading ? (
            <div className="px-3 py-2 text-[11px] text-ms-text-muted">Loading members…</div>
          ) : internalMembers.length === 0 ? (
            <div className="px-3 py-2 text-[11px] text-ms-text-muted">
              No project members to assign.
            </div>
          ) : (
            internalMembers.map((m) => {
              const isCurrent =
                m.id === currentAssigneeId && currentAssigneeKind !== 'external_user';
              return (
                <button
                  key={m.id}
                  type="button"
                  role="menuitem"
                  onClick={() => handlePick(m.id, 'user')}
                  disabled={isCurrent || reassign.isPending}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-[12px] hover:bg-ms-canvas disabled:cursor-not-allowed ${
                    isCurrent
                      ? 'bg-ms-surface-2 text-ms-text-strong font-semibold'
                      : 'text-ms-text-secondary'
                  }`}
                >
                  <Avatar name={m.name} colorSeq={m.colorSeq} avatarUrl={m.avatarUrl} size="xs" />
                  <span>{m.name}</span>
                  {isCurrent && (
                    <span className="ml-auto text-[10px] text-ms-text-muted">current</span>
                  )}
                </button>
              );
            })
          )
        )}

        {/* External members list — shown only on external tab of external tasks.
            MKS-513: loading, failed and genuinely empty are three distinct
            rows; with the flag off the first two can never be reached, so the
            branch collapses to the pre-MKS-513 empty-or-list rendering. */}
        {isExternalTask && activeTab === 'external' && (
          external.isLoading ? (
            <div className="px-3 py-2 text-[11px] text-ms-text-muted">
              Loading external members…
            </div>
          ) : external.isError ? (
            <div role="alert" className="px-3 py-2 text-[11px] text-red-600">
              Could not load external members.
            </div>
          ) : externalMemberList.length === 0 ? (
            <div className="px-3 py-2 text-[11px] text-ms-text-muted">
              No external members to assign.
            </div>
          ) : (
            externalMemberList.map((m) => {
              // Flag off: nothing is ever marked current on this tab, as today.
              const isCurrent =
                externalPicker &&
                currentAssigneeKind === 'external_user' &&
                m.id === currentExternalAssigneeId;
              return (
                <button
                  key={m.id}
                  type="button"
                  role="menuitem"
                  onClick={() => handlePick(m.id, 'external_user')}
                  disabled={isCurrent || reassign.isPending}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-[12px] hover:bg-ms-canvas disabled:cursor-not-allowed ${
                    isCurrent
                      ? 'bg-ms-surface-2 text-ms-text-strong font-semibold'
                      : 'text-ms-text-secondary'
                  }`}
                >
                  <Avatar name={m.name} size="xs" />
                  <span className="flex-1 truncate">{m.name}</span>
                  {isCurrent && (
                    <span className="text-[10px] text-ms-text-muted">current</span>
                  )}
                  <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700">
                    External
                  </span>
                </button>
              );
            })
          )
        )}
      </div>
    </details>
  );
}
